import { IAllFavoritesMatches, IFavoritesMatches } from '@/app/models/favorite';
import { EGoogleTagAnalyticsKeys } from '@/app/pages/sports/football/enums';
import { MatchApi } from '@/app/pages/sports/football/home/models';
import { inject, Injectable, signal } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { FsApiService } from '../auth/fs-api.service';
import { GoogleTagAnalyticsService } from '../google-tag';
import { FavoritesService } from './favorites.service';

const ID_LIST_MATCH_FAVORITES = 'idListMatchFavorites';

@Injectable({
  providedIn: 'root',
})
export class MatchFavoriteService {
  private googleTagAnalyticsService = inject(GoogleTagAnalyticsService);
  private fsApi = inject(FsApiService);
  favoritesService = inject(FavoritesService);

  isLoadingMatchesFavorites = signal<boolean>(false);
  isErrorMatchesFavorites = signal<boolean>(false);
  matchesFavorites = signal<IAllFavoritesMatches | null>(null);

  getAllFavoritesMatches() {
    this.isLoadingMatchesFavorites.set(true);
    this.isErrorMatchesFavorites.set(false);

    const pageSize = 10;
    let currentPage = 1;

    this.fsApi
      .get<IAllFavoritesMatches>(`/matches/favorites`, {
        params: { page: currentPage, size: pageSize },
      })
      .pipe(
        // Repite llamadas mientras haya más páginas
        expand((data) => {
          const res = data as IAllFavoritesMatches; // Forzamos el tipo
          if (!res || res.Page >= res.NumPages) {
            return EMPTY;
          }
          currentPage++;
          return this.fsApi.get<IAllFavoritesMatches>(`/matches/favorites`, {
            params: { page: currentPage, size: pageSize },
          }) as Observable<IAllFavoritesMatches>;
        }),
        // Acumula todos los resultados en un solo array
        reduce((acc: IFavoritesMatches[], data: IAllFavoritesMatches) => {
          return [...acc, ...data.Matches];
        }, []),
        // Manejo de errores
        catchError((err) => {
          console.error(err);
          this.isErrorMatchesFavorites.set(true);
          this.isLoadingMatchesFavorites.set(false);
          return of([]);
        }),
        // Transformar datos
        map((allMatches) => {
          return allMatches.map((match) => {
            this.favoritesService.checkFavoriteMatch(
              match.Id,
              match.IsFavorite
            );
            return {
              ...match,
              // Link: this.routingService.getTraslateUrlCompetitionDetails(
              //   competition.NameTranslation,
              //   competition.Name,
              //   competition.Id
              // ),
            };
          });
        })
      )
      .subscribe((matches) => {
        this.matchesFavorites.set({
          Matches: matches,
          EndIndex: matches.length - 1,
          NumPages: 1,
          Page: 1,
          PageSize: matches.length,
          StartIndex: 0,
          Total: matches.length,
        });
        this.isLoadingMatchesFavorites.set(false);
      });
  }

  setMatchFavorites(id: string, body: MatchApi) {
    if (!this.favoritesService.validAuthFavorites()) return;

    this.favoritesService.postFavoritesMatch(id);
    this.matchesFavorites.update((prev) => ({
      EndIndex: prev?.EndIndex ?? 0,
      NumPages: prev?.NumPages ?? 0,
      Page: prev?.Page ?? 0,
      PageSize: prev?.PageSize ?? 0,
      StartIndex: prev?.StartIndex ?? 0,
      Total: prev?.Total ?? 0,
      Matches: [body, ...(prev?.Matches ?? [])],
    }));

    // google analytics
    this.googleTagAnalyticsService.event(
      EGoogleTagAnalyticsKeys.ADD_MATCH_FAVORITES,
      {
        ...body,
        matchId: id,
        isFavorite: true,
      }
    );
  }

  deleteMatchFavorites(id: string, body: MatchApi) {
    if (!this.favoritesService.validAuthFavorites()) return;

    this.favoritesService.deleteFavoritesMatch(id);
    this.matchesFavorites.update((prev) => ({
      EndIndex: prev?.EndIndex ?? 0,
      NumPages: prev?.NumPages ?? 0,
      Page: prev?.Page ?? 0,
      PageSize: prev?.PageSize ?? 0,
      StartIndex: prev?.StartIndex ?? 0,
      Total: prev?.Total ?? 0,
      Matches: prev?.Matches.filter((match) => match.Id !== id) ?? [],
    }));

    // google analytics
    this.googleTagAnalyticsService.event(
      EGoogleTagAnalyticsKeys.REMOVE_MATCH_FAVORITES,
      {
        idMatch: id,
        ...body,
        isFavorite: false,
      }
    );
  }

  isMatchesCompetition(id: string) {
    return this.favoritesService.idsMatchesFavorites().includes(id);
  }
}
